import { useTranslation } from 'react-i18next';
import { SocialRewardCard, useSocialRewardController } from '@features/SocialReward';
import { trackEvent } from '@shared/analytics';
import { socialColors, socials, type SocialNetwork } from '@shared/config';
import { ChevronRightIcon, DzenIcon, TelegramIcon, VkIcon } from '@shared/ui';
import type { ComponentType, SVGProps } from 'react';

type Glyph = ComponentType<SVGProps<SVGSVGElement>>;

interface SocialLink {
  key: SocialNetwork;
  href: string;
  Glyph: Glyph;
  color: string;
}

const links: SocialLink[] = [
  { key: 'telegram', href: socials.telegram, Glyph: TelegramIcon, color: socialColors.telegram },
  { key: 'vk', href: socials.vk, Glyph: VkIcon, color: socialColors.vk },
  { key: 'dzen', href: socials.dzen, Glyph: DzenIcon, color: socialColors.dzen },
];

export default function CommunityPage() {
  const { t } = useTranslation();
  // Подарок за подписку — первым, над соцсетями (ТЗ главной § 11): с главной он переехал сюда и в
  // боковую панель. Ошибку статуса и «спасибо» после использования показывает только эта карточка.
  const reward = useSocialRewardController();
  return (
    <div className="mx-auto max-w-[760px] animate-fadeIn px-4 pb-28 pt-16 sm:px-6 nav:px-12 nav:pb-20 nav:pt-10">
      <header className="mb-9">
        <h1 className="text-balance pb-1.5 font-serif text-[30px] font-medium leading-[1.2] tracking-[-0.02em] sm:text-[36px] nav:text-[44px]">
          {t('community.title')}
        </h1>
        <div className="mt-2.5 max-w-[52ch] text-[14px] leading-relaxed tracking-[0.02em] text-muted">
          {t('community.sub')}
        </div>
      </header>

      <div className="flex flex-col gap-3">
        {reward && (
          <SocialRewardCard reward={reward} onOpen={() => trackEvent('social_reward_opened', { place: 'community' })} />
        )}
        {links.map(({ key, href, Glyph, color }) => (
          <a
            key={key}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-4 rounded-card border border-transparent bg-card p-4 transition-colors hover:border-line sm:p-5"
          >
            <span
              className="grid h-12 w-12 shrink-0 place-items-center rounded-[14px]"
              style={{ backgroundColor: `${color}1f`, color }}
            >
              <Glyph className="h-6 w-6" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-[15px] font-medium text-fg">{t(`community.${key}.name`)}</span>
              <span className="mt-0.5 block truncate text-[14px] text-muted">{t(`community.${key}.desc`)}</span>
            </span>
            <span className="ml-auto hidden shrink-0 items-center gap-1 text-[14px] font-medium text-muted transition-colors group-hover:text-fg sm:inline-flex">
              {t('community.openAction')}
              <ChevronRightIcon className="h-4 w-4" />
            </span>
            <ChevronRightIcon className="h-5 w-5 shrink-0 text-muted transition-colors group-hover:text-fg sm:hidden" />
          </a>
        ))}
      </div>
    </div>
  );
}
